import React, { useState, useCallback } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { ReportsListPage } from '../ui/diary/ReportsListPage';
import type { Report, ReportsResponse, ReportsListLabels } from '../shared/types/reports';

// Create a query client for this component tree
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

export interface ReportsListPageContainerProps {
  graphqlEndpoint: string;
  csrfToken: string;
  reportUrlTemplate: string; // e.g., "/diary/reports/:id"
  statsUrl: string;
  diaryUrl: string;
  labels: ReportsListLabels;
}

// GraphQL Query
const GET_REPORTS_QUERY = `
  query GetReports($page: Int, $perPage: Int, $periodType: String) {
    reports(page: $page, perPage: $perPage, periodType: $periodType) {
      reports {
        id
        periodType
        periodStart
        periodEnd
        generatedAt
        viewedAt
        unviewed
        moodChange
        moodCountChange
        currentPeriodStats {
          periodStart
          periodEnd
          averageMood
          moodCount
          moodTimeline {
            date
            mood
          }
        }
        previousPeriodStats {
          averageMood
          moodCount
        }
        aiReport {
          id
          title
          firstSentence
        }
      }
      page
      perPage
      hasMore
      totalCount
    }
  }
`;

export type ReportFilter = 'all' | 'week' | 'month';

function ReportsListPageInner({
  graphqlEndpoint,
  csrfToken,
  reportUrlTemplate,
  statsUrl,
  diaryUrl,
  labels,
}: ReportsListPageContainerProps) {
  const [filter, setFilter] = useState<ReportFilter>('all');
  const [page, setPage] = useState(1);
  const perPage = 10;

  // Fetch reports data
  const { data, isLoading } = useQuery({
    queryKey: ['reports', filter, page],
    queryFn: async () => {
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          query: GET_REPORTS_QUERY,
          variables: {
            page,
            perPage,
            periodType: filter === 'all' ? null : filter,
          },
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch reports');
      }

      const result = await response.json();
      if (result.errors) {
        throw new Error(result.errors[0]?.message || 'GraphQL error');
      }

      return result.data.reports as ReportsResponse;
    },
  });

  const handleFilterChange = useCallback((newFilter: ReportFilter) => {
    setFilter(newFilter);
    setPage(1); // Reset to first page when filter changes
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const getReportUrl = useCallback((reportId: string) => {
    return reportUrlTemplate.replace(':id', reportId);
  }, [reportUrlTemplate]);

  return (
    <ReportsListPage
      reports={data?.reports || []}
      totalCount={data?.totalCount || 0}
      page={page}
      perPage={perPage}
      hasMore={data?.hasMore || false}
      filter={filter}
      onFilterChange={handleFilterChange}
      onPageChange={handlePageChange}
      getReportUrl={getReportUrl}
      statsUrl={statsUrl}
      diaryUrl={diaryUrl}
      labels={labels}
      isLoading={isLoading}
    />
  );
}

/**
 * ReportsListPageContainer - Entry point for rendering reports list page in Rails
 */
export function ReportsListPageContainer(props: ReportsListPageContainerProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <ReportsListPageInner {...props} />
    </QueryClientProvider>
  );
}

/**
 * Mount function for rendering from Rails
 */
export function mountReportsListPage(
  container: HTMLElement,
  props: ReportsListPageContainerProps
) {
  // Clean up existing root if any (for Turbo navigation)
  const existingRoot = (container as any).__reactRoot;
  if (existingRoot) {
    existingRoot.unmount();
  }

  const root = createRoot(container);
  (container as any).__reactRoot = root;
  root.render(<ReportsListPageContainer {...props} />);
  return root;
}

// Expose mount function to window for Rails integration
if (typeof window !== 'undefined') {
  (window as any).mountReportsListPage = mountReportsListPage;
}

export default ReportsListPageContainer;
